import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, ShieldCheck, KeyRound, Smartphone, CheckCircle2, AlertCircle } from "lucide-react";
import { orgApi, saveOrganizerToken } from "../lib/organizerApi";
import { useOrganizerAuth } from "../auth/OrganizerAuthContext";
import PasswordInput from "../components/PasswordInput";
import OtpInput from "../components/OtpInput";

/**
 * Magic-link Setup Wizard — Phase 6.2.
 *
 * Flow:
 *   1. Arrive with ?token=... → redeem to set password (and optionally name).
 *      Backend marks email as verified and returns a fresh JWT.
 *   2. Phone OTP verify (start → enter 6-digit code → confirm).
 *   3. Redirect to /organizer/dashboard.
 */
export default function OrganizerSetup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useOrganizerAuth();
  const token = params.get("token") || "";

  const [step, setStep] = useState(token ? "password" : "invalid");
  const [organizer, setOrganizer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // Step 1 — set password
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [contactName, setContactName] = useState("");

  // Step 2 — phone OTP
  const [otp, setOtp] = useState("");
  const [otpStarted, setOtpStarted] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Mật khẩu phải có ít nhất 8 ký tự."); return; }
    if (pw !== pw2) { setError("Hai mật khẩu không khớp. Vui lòng nhập lại."); return; }
    setBusy(true);
    try {
      const payload = { token, password: pw };
      if (contactName.trim()) payload.contact_name = contactName.trim();
      const { data } = await orgApi.post("/organizer/setup-link/redeem", payload);
      saveOrganizerToken(data.access_token);
      await refresh();
      setOrganizer(data.organizer);
      // Skip phone OTP if already verified or no phone on file
      if (data.organizer?.verified_phone_at) {
        setStep("done");
      } else if (!data.organizer?.contact_phone) {
        setStep("done");
      } else {
        setStep("phone");
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      if (status === 404) setError("Liên kết không hợp lệ. Vui lòng yêu cầu liên kết mới.");
      else if (status === 409) setError("Liên kết đã được sử dụng. Hãy đăng nhập bằng email và mật khẩu của bạn.");
      else if (status === 410) setError("Liên kết đã hết hạn. Vui lòng yêu cầu liên kết mới.");
      else setError(detail || "Không thể hoàn tất. Vui lòng thử lại.");
    } finally {
      setBusy(false);
    }
  };

  const startOtp = async () => {
    setError(""); setInfo("");
    setBusy(true);
    try {
      const { data } = await orgApi.post("/organizer/verify-phone/start", {});
      if (data?.status === "skipped") {
        setInfo("Hệ thống SMS chưa được cấu hình. Bạn có thể bỏ qua bước này.");
      } else {
        setInfo("Đã gửi mã OTP đến số điện thoại của bạn. Vui lòng kiểm tra tin nhắn.");
      }
      setOtpStarted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Không thể gửi mã OTP. Vui lòng thử lại.");
    } finally {
      setBusy(false);
    }
  };

  const confirmOtp = async (e, override = null) => {
    e?.preventDefault?.();
    setError(""); setInfo("");
    const code = (override ?? otp).trim();
    if (code.length < 4) { setError("Vui lòng nhập mã OTP."); return; }
    setBusy(true);
    try {
      const { data } = await orgApi.post("/organizer/verify-phone/confirm", { code });
      setOrganizer(data.organizer);
      setStep("done");
    } catch (err) {
      setError(err?.response?.data?.detail || "Mã OTP không đúng hoặc đã hết hạn.");
      setOtp("");  // clear so user can re-enter without backspacing 6 boxes
    } finally {
      setBusy(false);
    }
  };

  // Auto-start OTP once we land on phone step
  useEffect(() => {
    if (step === "phone" && !otpStarted) {
      startOtp();
    }
  }, [step]);

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center px-4 py-12" data-testid="organizer-setup-page">
      <div className="w-full max-w-xl">
        <Link to="/" className="inline-flex items-center gap-2 text-espresso-soft hover:text-espresso text-sm">
          <Sparkles className="w-4 h-4" />
          <span className="tracking-[0.28em] uppercase">Nail Viet Club</span>
        </Link>

        <div className="mt-6 bg-white border border-espresso/10 rounded-3xl p-7 sm:p-10 shadow-sm">
          <Stepper step={step} />

          {step === "invalid" && (
            <div className="text-center" data-testid="setup-invalid">
              <AlertCircle className="w-10 h-10 text-espresso-soft mx-auto" />
              <h1 className="mt-3 text-2xl font-serif text-espresso">Liên kết không hợp lệ</h1>
              <p className="mt-2 text-sm text-espresso-soft">
                Liên kết thiết lập tài khoản đã thiếu hoặc sai. Vui lòng yêu cầu liên kết mới.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
                <Link to="/organizer/login" className="inline-flex items-center justify-center gap-2 border border-espresso/20 text-espresso rounded-full px-5 py-2.5 text-sm font-semibold">
                  Đăng nhập
                </Link>
                <Link to="/organizer/signup" className="inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-sm font-semibold">
                  Tạo tài khoản mới
                </Link>
              </div>
            </div>
          )}

          {step === "password" && (
            <form onSubmit={submitPassword} className="space-y-4" data-testid="setup-password-form">
              <div>
                <h1 className="text-2xl sm:text-3xl font-serif text-espresso">Hoàn tất tài khoản của bạn</h1>
                <p className="mt-2 text-sm text-espresso-soft">
                  Bạn chỉ còn vài bước để bắt đầu tổ chức sự kiện trên Nail Viet Club. Hãy đặt mật khẩu an toàn để đăng nhập sau này.
                </p>
              </div>

              <Field label="Họ và tên người liên hệ (tùy chọn)">
                <input
                  data-testid="setup-contact-name"
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Ví dụ: Nguyễn Văn A"
                  className="input-text"
                  autoComplete="name"
                />
              </Field>

              <Field label="Mật khẩu mới *">
                <PasswordInput
                  data-testid="setup-password"
                  testIdSuffix="setup-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Tối thiểu 8 ký tự"
                  leftIcon={null}
                  className="input-text w-full"
                  minLength={8}
                  required
                  autoComplete="new-password"
                />
              </Field>

              <Field label="Nhập lại mật khẩu *">
                <PasswordInput
                  data-testid="setup-password-confirm"
                  testIdSuffix="setup-password-confirm"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  placeholder="Nhập lại mật khẩu"
                  leftIcon={null}
                  className="input-text w-full"
                  minLength={8}
                  required
                  autoComplete="new-password"
                />
              </Field>

              {error && <ErrorBox>{error}</ErrorBox>}

              <button
                type="submit"
                disabled={busy}
                data-testid="setup-submit-password"
                className="w-full inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                {busy ? "Đang lưu…" : "Đặt mật khẩu & tiếp tục"}
              </button>

              <p className="text-xs text-espresso-muted flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                Email của bạn sẽ được xác minh tự động qua liên kết này.
              </p>
            </form>
          )}

          {step === "phone" && (
            <form onSubmit={confirmOtp} className="space-y-4" data-testid="setup-phone-form">
              <div>
                <h1 className="text-2xl sm:text-3xl font-serif text-espresso">Xác minh số điện thoại</h1>
                <p className="mt-2 text-sm text-espresso-soft">
                  Chúng tôi đã gửi mã OTP 6 chữ số đến{" "}
                  <span className="font-semibold text-espresso">{organizer?.contact_phone || "số điện thoại của bạn"}</span>.
                  Hãy nhập mã để hoàn tất.
                </p>
              </div>

              <Field label="Mã OTP *">
                <OtpInput
                  length={6}
                  value={otp}
                  onChange={setOtp}
                  onComplete={(code) => confirmOtp(null, code)}
                  loading={busy}
                  error={!!error}
                  testIdPrefix="setup-otp"
                  ariaLabel="Mã OTP xác minh số điện thoại"
                />
              </Field>

              {info && <InfoBox>{info}</InfoBox>}
              {error && <ErrorBox>{error}</ErrorBox>}

              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="setup-submit-otp"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
                  Xác minh
                </button>
                <button
                  type="button"
                  onClick={startOtp}
                  disabled={busy}
                  data-testid="setup-resend-otp"
                  className="inline-flex items-center justify-center gap-2 border border-espresso/20 text-espresso rounded-full px-5 py-3 text-sm font-semibold disabled:opacity-60"
                >
                  Gửi lại mã
                </button>
              </div>

              <button
                type="button"
                onClick={() => setStep("done")}
                data-testid="setup-skip-phone"
                className="w-full text-center text-xs uppercase tracking-[0.22em] text-espresso-muted hover:text-espresso"
              >
                Tạm bỏ qua, xác minh sau
              </button>
            </form>
          )}

          {step === "done" && (
            <div className="text-center py-4" data-testid="setup-done">
              <div className="mx-auto w-16 h-16 rounded-full bg-[#E1EDE6] text-[#2D4A3E] flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h1 className="mt-4 text-2xl sm:text-3xl font-serif text-espresso">Hoàn tất thiết lập!</h1>
              <p className="mt-2 text-sm text-espresso-soft">
                Tài khoản của bạn đã sẵn sàng. Hãy vào bảng điều khiển để tạo sự kiện đầu tiên.
              </p>
              <button
                onClick={() => navigate("/organizer/dashboard", { replace: true })}
                data-testid="setup-go-dashboard"
                className="mt-6 inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-6 py-3 text-sm font-semibold"
              >
                Mở bảng điều khiển
              </button>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-espresso-muted">
          Bạn cần trợ giúp?{" "}
          <Link to="/contact" className="text-espresso underline-offset-2 hover:underline">
            Liên hệ đội ngũ Nail Viet Club
          </Link>
        </p>
      </div>

      <style>{`
        .input-text {
          width: 100%;
          background: #FAF7F1;
          border: 1px solid rgba(60, 40, 28, 0.15);
          border-radius: 12px;
          padding: 12px 14px;
          font-size: 14px;
          color: #3C281C;
          outline: none;
          transition: border-color .15s ease, box-shadow .15s ease;
        }
        .input-text:focus {
          border-color: #3C281C;
          box-shadow: 0 0 0 3px rgba(60, 40, 28, 0.08);
          background: #FFFFFF;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-espresso-soft mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function ErrorBox({ children }) {
  return (
    <div data-testid="setup-error" className="rounded-xl border border-red-200 bg-red-50 text-red-800 px-4 py-2.5 text-sm">
      {children}
    </div>
  );
}

function InfoBox({ children }) {
  return (
    <div data-testid="setup-info" className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-2.5 text-sm">
      {children}
    </div>
  );
}

function Stepper({ step }) {
  const stages = [
    { key: "password", label: "Mật khẩu" },
    { key: "phone",    label: "Số điện thoại" },
    { key: "done",     label: "Hoàn tất" },
  ];
  const idx = Math.max(0, stages.findIndex((s) => s.key === step));
  if (step === "invalid") return null;
  return (
    <div className="flex items-center gap-2 mb-6" data-testid="setup-stepper">
      {stages.map((s, i) => {
        const active = i <= idx;
        return (
          <div key={s.key} className="flex-1">
            <div className={`h-1 rounded-full ${active ? "bg-espresso" : "bg-espresso/15"}`} />
            <div className={`mt-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold ${active ? "text-espresso" : "text-espresso-muted"}`}>
              {i + 1}. {s.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}
